// ─── 💶 Il conto coi maestri, come lo vede la segreteria (tappa ⑧b) ──────────
//
// 🗣️ *«io affitto il campo al maestro […] costo della lezione oppure del corso,
// costo del campo, la differenza va al maestro»* · *«incasso io dagli allievi,
// non il maestro»* · *«io si intende la segreteria»* (20/09/2026).
//
// ⇒ **Tre numeri, e due esistevano già**: ① quanto hanno a carico gli allievi
//   (`booking_players.amount_due_cents`, sommato dalla tappa ④b) · ② quanto
//   costa il campo (la tappa ⑧a) · ③ la differenza, che il circolo paga al
//   maestro. Il maestro non versa niente a nessuno.
//
// 🚨⭐⭐ **QUESTO FILE È DELLA SEGRETERIA, e NON si riusa nell'area maestro.**
//    Là vale la riga scritta in testa a `maestro.ts`: *«uno schema riusato è un
//    canale riusato»*. ⇒ Un maestro non può vedere la riga di un altro **per
//    costruzione** — perché lo schema da cui esce la sua risposta non sa
//    nemmeno nominarla — non perché una schermata la nasconde.
//    ⚖️ Ciò che le due sponde condividono è il **frammento SQL** che sceglie la
//    tariffa (⇒ `modules/conto/deposito.ts`), cioè la regola: quella sì, una
//    sola, o un giorno le due schermate direbbero due cifre per lo stesso mese.
import { z } from 'zod';
import { trattenutaDelMaestroSchema } from './organizzatore.js';
import { dataSchema } from './circolo.js';
/**
 * ⚠️ La stessa regex di `domandaSoldiSchema` (⇒ `maestro.ts`), e non è una
 * copia per distrazione: le due rotte prendono lo **stesso** mese e devono
 * rifiutare le stesse cose. Un `2026-13` accettato di qua e no di là sarebbe
 * una differenza che si scopre a schermo.
 */
export const domandaContoSchema = z.object({
    mese: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Serve un mese come 2026-09'),
});
/**
 * Una riga del conto: un maestro, un mese.
 *
 * ⏳ **Ci sono dentro SOLO le volte già fatte e coperte da una tariffa**, e le
 * due parole contano tutt'e due:
 * · *già fatte* — `ends_at <= now()`: quel campo l'ha usato davvero;
 * · *coperte* — c'era una tariffa quel giorno.
 *
 * 🚨⭐⭐ **`scoperte` NON È UN DETTAGLIO: È LA GUARDIA DI QUESTO SCHEMA.** Una
 *    volta senza tariffa non entra in **nessuna** delle tre cifre — né allievi,
 *    né campo, né differenza. Se entrasse solo negli allievi (che è ciò che
 *    succede da sé, perché `sum()` salta i `null` del campo) il conto uscirebbe
 *    **più alto a favore del maestro**, con dei numeri perfettamente formattati
 *    e nessun errore da nessuna parte.
 * 📌 *Un dato che manca si dichiara mancante: arrotondato a zero diventa una
 *    risposta sbagliata invece di una domanda.*
 */
export const rigaContoMaestroSchema = z.object({
    maestroId: z.uuid(),
    nome: z.string(),
    volte: z.number().int(),
    scoperte: z.number().int(),
    minuti: z.number().int(),
    allieviCents: z.number().int(),
    campoCents: z.number().int(),
    /** 🆕 Fase 3 (R6): le lezioni della sera disdette tardi o saltate, tolte dal compenso. */
    trattenuteCents: z.number().int().default(0),
    restaCents: z.number().int(),
});
/**
 * Una tariffa che ha davvero governato qualche volta di questo mese.
 *
 * ⚖️ Non è l'elenco delle tariffe scritte: è quello di **quelle usate**. La
 * schermata nomina la cifra all'ora solo quando ne è valsa **una sola** — con
 * due, dire «20,00 € all'ora» sarebbe vero per metà del mese e falso per
 * l'altra, e chi controlla il conto moltiplicando non ritroverebbe il totale.
 */
export const tariffaUsataSchema = z.object({
    centsPerHour: z.number().int(),
    validFrom: dataSchema,
});
/**
 * Il mese intero, come lo legge la segreteria.
 *
 * ⛔ **Nessun totale dal server**, ed è la stessa riga già scritta sopra
 *    `soldiDelMeseSchema`: un totale mandato da fuori è un numero che un giorno
 *    può non tornare con le righe che lo compongono, e a leggerlo è qualcuno
 *    che deve fidarsi. Lo somma chi lo mostra, dove si vede subito se non torna.
 * ⏳ `daFareVolte` sono le volte di **questo mese** che devono ancora succedere:
 *    non si fatturano — quel campo non l'ha ancora usato nessuno — ma si dicono,
 *    o a metà mese la cifra sembra definitiva.
 * 🧾 `tariffe` a zero **con delle volte in elenco** è la scena C: non c'è niente
 *    da cui partire, e il conto non si fa.
 * ⭐ `primo` e `ultimo` sono i mesi estremi in cui il circolo ha avuto qualcosa
 *    con un maestro: servono alle due frecce, che si spengono lì. Sono `null`
 *    quando non c'è mai stato niente — che è una cosa diversa da «questo mese
 *    zero».
 */
export const contoDeiMaestriSchema = z.object({
    mese: z.string(),
    righe: z.array(rigaContoMaestroSchema),
    tariffe: z.array(tariffaUsataSchema),
    tariffeInTutto: z.number().int(),
    daFareVolte: z.number().int(),
    primo: z.string().nullable(),
    ultimo: z.string().nullable(),
    /** 🆕 Fase 3 (R6): le trattenute del mese, una per riga, col perché. */
    trattenute: z.array(trattenutaDelMaestroSchema).default([]),
});
//# sourceMappingURL=conto.js.map